/**
 * Rating Committee Dashboard
 *
 * Landing page for Rating Committee members.
 * Shows reports pending final approval and recent approvals.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRoleAssumption } from '../../context/RoleAssumptionContext';
import { getRatedInstitutionsWithRatings } from '../../services/institutionService';
import './DashboardStyles.css';

interface InstitutionSummary {
  id: string;
  name: string;
  rating: string;
  score: number;
}

export default function CommitteeDashboard() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { effectiveOrganization } = useRoleAssumption();
  const { t } = useTranslation();
  const [institutions, setInstitutions] = useState<InstitutionSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const firstName = userProfile?.display_name?.split(' ')[0] || 'Committee Member';
  const orgName = effectiveOrganization?.name || 'Your CRA';

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);

        // Get institutions from BigQuery API
        // Filter by organization.institutions_access if available
        const accessibleIds = effectiveOrganization?.institutions_access || [];
        const ratedInstitutions = await getRatedInstitutionsWithRatings();
        const allInstitutions = ratedInstitutions
          .filter((inst) => accessibleIds.length === 0 || accessibleIds.includes(inst.institution_id))
          .map((inst) => ({
            id: inst.institution_id,
            name: inst.institution_name,
            rating: inst.rating_category || 'N/A',
            score: inst.numeric_score || 0,
          }));
        setInstitutions(allInstitutions);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [effectiveOrganization]);

  const getRatingClass = (rating: string): string => {
    const r = rating.toLowerCase().replace('+', '').replace('-', '');
    return `rating-${r}`;
  };

  // Placeholder: In Phase 4, this will come from report_workflows collection
  const pendingApprovals = 0;
  const recentApprovals = 0;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>{t('dashboard.welcomeBack', { name: firstName })}</h1>
        <p className="dashboard-subtitle">{orgName} - {t('dashboard.committeeSubtitle')}</p>
      </div>

      <div className="dashboard-content">
        {/* Quick Actions */}
        <div className="dashboard-section">
          <h2 className="section-title">{t('dashboard.quickActions')}</h2>
          <div className="actions-grid">
            <button className="action-card primary" onClick={() => navigate('/reports')}>
              <span className="action-label">{t('dashboard.reviewReports')}</span>
            </button>
            <button className="action-card" onClick={() => navigate('/ratings')}>
              <span className="action-label">{t('dashboard.allRatings')}</span>
            </button>
            <button className="action-card" onClick={() => navigate('/settings')}>
              <span className="action-label">{t('settings.title')}</span>
            </button>
          </div>
        </div>

        {/* Committee Stats */}
        <div className="dashboard-section">
          <h2 className="section-title">{t('dashboard.committeeOverview')}</h2>
          <div className="stats-grid">
            <div className="stat-card highlight">
              <div className="stat-value">{loading ? '...' : pendingApprovals}</div>
              <div className="stat-label">{t('dashboard.pendingApprovals')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{loading ? '...' : recentApprovals}</div>
              <div className="stat-label">{t('dashboard.recentApprovals')}</div>
            </div>
            <div
              className="stat-card"
              onClick={() => navigate('/ratings')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-value">{loading ? '...' : institutions.length}</div>
              <div className="stat-label">{t('dashboard.totalInstitutions')}</div>
            </div>
          </div>
        </div>

        {/* Pending Approvals */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">{t('dashboard.pendingCommitteeApproval')}</h2>
          </div>
          {pendingApprovals === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">✓</span>
              <h3>{t('dashboard.noPendingApprovals')}</h3>
              <p>{t('dashboard.allReportsReviewed')}</p>
            </div>
          ) : (
            <div className="institution-list">
              {/* Placeholder: In Phase 4, this will show actual pending reports */}
            </div>
          )}
        </div>

        {/* Recent Ratings */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">{t('dashboard.recentRatings')}</h2>
            <button
              className="section-action"
              onClick={() => navigate('/ratings')}
            >
              {t('common.viewAll')}
            </button>
          </div>
          <div className="institution-list">
            {institutions.slice(0, 5).map((inst) => (
              <div
                key={inst.id}
                className="institution-item"
                onClick={() => navigate(`/analyst-bench/${inst.id}`)}
              >
                <div className={`institution-rating ${getRatingClass(inst.rating)}`}>
                  {inst.rating}
                </div>
                <div className="institution-info">
                  <div className="institution-name">{inst.name}</div>
                  <div className="institution-score">{t('dashboard.score', { value: inst.score.toFixed(1) })}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
